import React, { useState, useEffect } from 'react';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { useTheme } from '../contexts/ThemeContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Registrar componentes Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usuariosData, setUsuariosData] = useState([]);
  const [horariosData, setHorariosData] = useState([]);
  const [textosData, setTextosData] = useState([]);
  const [diariasData, setDiariasData] = useState([]);
  const [variaveisData, setVariaveisData] = useState([]);
  const [tempoData, setTempoData] = useState([]);
  const { darkMode } = useTheme();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [usuariosRes, horariosRes, textosRes, diariasRes, variaveisRes, tempoRes] = await Promise.all([
          api.get('/estatisticas/usuarios'),
          api.get('/estatisticas/horarios'),
          api.get('/estatisticas/textos'),
          api.get('/estatisticas/diarias'),
          api.get('/estatisticas/variaveis'),
          api.get('/estatisticas/tempo'),
        ]);

        setUsuariosData(usuariosRes.data);
        setHorariosData(horariosRes.data);
        setTextosData(textosRes.data);
        setDiariasData(diariasRes.data);
        setVariaveisData(variaveisRes.data);
        setTempoData(tempoRes.data);
        setError(null);
      } catch (err) {
        console.error('Erro ao carregar estatísticas:', err);
        setError('Erro ao carregar estatísticas. Por favor, tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Formatação de dados para gráficos
  const usuariosChartData = {
    labels: usuariosData.slice(0, 10).map(item => item.username),
    datasets: [
      {
        label: 'Total de Substituições',
        data: usuariosData.slice(0, 10).map(item => item.total_substituicoes),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
    ],
  };

  const horariosChartData = {
    labels: horariosData.map(item => `${item.hora}h`),
    datasets: [
      {
        label: 'Substituições por Hora',
        data: horariosData.map(item => item.total),
        backgroundColor: 'rgba(153, 102, 255, 0.6)',
        borderColor: 'rgba(153, 102, 255, 1)',
        borderWidth: 1,
        fill: true,
      },
    ],
  };

  const textosChartData = {
    labels: textosData.slice(0, 10).map(item => item.texto_id),
    datasets: [
      {
        label: 'Total de Substituições',
        data: textosData.slice(0, 10).map(item => item.total_substituicoes),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
          'rgba(255, 159, 64, 0.6)',
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const diariasChartData = {
    labels: diariasData.map(item => new Date(item.data).toLocaleDateString('pt-BR')),
    datasets: [
      {
        label: 'Substituições por Dia',
        data: diariasData.map(item => item.total),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
        tension: 0.1,
      },
    ],
  };

  // Formatação de dados para gráfico de variáveis
  const variaveisChartData = {
    labels: variaveisData.slice(0, 10).map(item => item.nome_variavel),
    datasets: [
      {
        label: 'Uso de Variáveis',
        data: variaveisData.slice(0, 10).map(item => item.total),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
          'rgba(255, 159, 64, 0.6)',
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  // Formatação de dados para gráfico de tempo médio
  const tempoChartData = {
    labels: tempoData.slice(0, 8).map(item => item.texto_id),
    datasets: [
      {
        label: 'Tempo Médio (segundos)',
        data: tempoData.slice(0, 8).map(item => parseFloat(item.tempo_medio).toFixed(1)),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <LoadingSpinner />
        <p className="mt-4 text-gray-600 dark:text-gray-300">Carregando estatísticas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 dark:bg-red-900/50 border-l-4 border-red-500 text-red-700 dark:text-red-200 p-5 mb-4 rounded-lg shadow">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Dashboard - Estatísticas de Uso</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Cards de estatísticas resumidas */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl shadow-md text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium mb-1">Total de Substituições</p>
              <h3 className="text-3xl font-bold">{usuariosData.reduce((total, user) => total + user.total_substituicoes, 0)}</h3>
            </div>
            <div className="p-3 bg-white/20 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-xl shadow-md text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium mb-1">Usuários Ativos</p>
              <h3 className="text-3xl font-bold">{usuariosData.length}</h3>
            </div>
            <div className="p-3 bg-white/20 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-xl shadow-md text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium mb-1">Total de Textos</p>
              <h3 className="text-3xl font-bold">{textosData.length}</h3>
            </div>
            <div className="p-3 bg-white/20 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usuários com mais substituições */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border border-gray-100 dark:border-gray-700 hover:shadow-lg transition-shadow duration-300">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Top Usuários
          </h2>
          {usuariosData.length > 0 ? (
            <div>
              <div className="h-64">
                <Bar 
                  data={usuariosChartData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: 'rgba(200, 200, 200, 0.2)',
                        },
                        ticks: {
                          precision: 0,
                        }
                      },
                      x: {
                        grid: {
                          display: false
                        }
                      }
                    },
                    plugins: {
                      title: {
                        display: true,
                        text: 'Usuários com mais substituições',
                        color: darkMode ? '#e2e8f0' : '#1e293b',
                        font: {
                          size: 14,
                          weight: 'normal'
                        }
                      },
                      legend: {
                        display: true,
                        position: 'bottom',
                        labels: {
                          color: darkMode ? '#e2e8f0' : '#1e293b',
                          usePointStyle: true,
                          boxWidth: 6
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
              <p className="text-gray-500 dark:text-gray-400">Sem dados disponíveis</p>
            </div>
          )}
        </div>

        {/* Substituições por hora */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border border-gray-100 dark:border-gray-700 hover:shadow-lg transition-shadow duration-300">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Horários de Uso
          </h2>
          {horariosData.length > 0 ? (
            <div className="h-64">
              <Line
                data={horariosChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(200, 200, 200, 0.2)',
                      },
                      ticks: {
                        precision: 0
                      }
                    },
                    x: {
                      grid: {
                        display: false
                      }
                    }
                  },
                  elements: {
                    line: {
                      tension: 0.4
                    },
                    point: {
                      radius: 4,
                      hoverRadius: 6
                    }
                  },
                  plugins: {
                    title: {
                      display: true,
                      text: 'Distribuição por hora do dia',
                      color: darkMode ? '#e2e8f0' : '#1e293b',
                      font: {
                        size: 14,
                        weight: 'normal'
                      }
                    },
                    legend: {
                      display: true,
                      position: 'bottom',
                      labels: {
                        color: darkMode ? '#e2e8f0' : '#1e293b',
                        usePointStyle: true,
                        boxWidth: 6
                      }
                    }
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
              <p className="text-gray-500 dark:text-gray-400">Sem dados disponíveis</p>
            </div>
          )}
        </div>

        {/* Textos mais utilizados */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border border-gray-100 dark:border-gray-700 hover:shadow-lg transition-shadow duration-300">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Textos Mais Utilizados
          </h2>
          {textosData.length > 0 ? (
            <div className="h-64">
              <Pie
                data={textosChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    title: {
                      display: true,
                      text: 'Top 10 textos mais utilizados',
                      color: darkMode ? '#e2e8f0' : '#1e293b',
                      font: {
                        size: 14,
                        weight: 'normal'
                      }
                    },
                    legend: {
                      display: true,
                      position: 'right',
                      labels: {
                        color: darkMode ? '#e2e8f0' : '#1e293b',
                        usePointStyle: true,
                        boxWidth: 6
                      }
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          const value = context.raw;
                          const total = context.chart._metasets[context.datasetIndex].total;
                          const percentage = Math.round(value / total * 100) + '%';
                          return `${context.label}: ${value} (${percentage})`;
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
              <p className="text-gray-500 dark:text-gray-400">Sem dados disponíveis</p>
            </div>
          )}
        </div>

        {/* Substituições por dia */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border border-gray-100 dark:border-gray-700 hover:shadow-lg transition-shadow duration-300">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Atividade Diária
          </h2>
          {diariasData.length > 0 ? (
            <div className="h-64">
              <Line
                data={diariasChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(200, 200, 200, 0.2)',
                      }
                    },
                    x: {
                      grid: {
                        display: false
                      }
                    }
                  },
                  elements: {
                    line: {
                      tension: 0.4,
                      fill: true,
                    },
                    point: {
                      radius: 3,
                      hoverRadius: 5
                    }
                  },
                  plugins: {
                    title: {
                      display: true,
                      text: 'Substituições por dia',
                      color: darkMode ? '#e2e8f0' : '#1e293b',
                      font: {
                        size: 14,
                        weight: 'normal'
                      }
                    },
                    legend: {
                      display: true,
                      position: 'bottom',
                      labels: {
                        color: darkMode ? '#e2e8f0' : '#1e293b',
                        usePointStyle: true,
                        boxWidth: 6
                      }
                    }
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
              <p className="text-gray-500 dark:text-gray-400">Sem dados disponíveis</p>
            </div>
          )}
        </div>

        {/* Uso de variáveis */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border border-gray-100 dark:border-gray-700 hover:shadow-lg transition-shadow duration-300">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Variáveis Mais Utilizadas
          </h2>
          {variaveisData.length > 0 ? (
            <div className="h-64">
              <Doughnut
                data={variaveisChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: '60%',
                  plugins: {
                    title: {
                      display: true,
                      text: 'Top 10 variáveis mais usadas',
                      color: darkMode ? '#e2e8f0' : '#1e293b',
                      font: {
                        size: 14,
                        weight: 'normal'
                      }
                    },
                    legend: {
                      display: true,
                      position: 'right',
                      labels: {
                        color: darkMode ? '#e2e8f0' : '#1e293b',
                        usePointStyle: true,
                        boxWidth: 6
                      }
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          const value = context.raw;
                          const total = context.chart._metasets[context.datasetIndex].total;
                          const percentage = Math.round(value / total * 100) + '%';
                          return `${context.label}: ${value} (${percentage})`;
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
              <p className="text-gray-500 dark:text-gray-400">Sem dados disponíveis</p>
            </div>
          )}
        </div>

        {/* Tempo médio de substituição */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border border-gray-100 dark:border-gray-700 hover:shadow-lg transition-shadow duration-300">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-cyan-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Tempo Médio de Substituição
          </h2>
          {tempoData.length > 0 ? (
            <div className="h-64">
              <Bar
                data={tempoChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(200, 200, 200, 0.2)',
                      },
                      title: {
                        display: true,
                        text: 'Segundos',
                        color: darkMode ? '#e2e8f0' : '#1e293b',
                      }
                    },
                    x: {
                      grid: {
                        display: false
                      }
                    }
                  },
                  plugins: {
                    title: {
                      display: true,
                      text: 'Tempo médio por texto (segundos)',
                      color: darkMode ? '#e2e8f0' : '#1e293b',
                      font: {
                        size: 14,
                        weight: 'normal'
                      }
                    },
                    legend: {
                      display: true,
                      position: 'bottom',
                      labels: {
                        color: darkMode ? '#e2e8f0' : '#1e293b',
                        usePointStyle: true,
                        boxWidth: 6
                      }
                    }
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
              <p className="text-gray-500 dark:text-gray-400">Sem dados disponíveis</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 